import { useMemo } from "react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { analyzePasswordStrength } from "@/utils/securityUtils";

interface PasswordStrengthIndicatorProps {
  password: string;
  personalInfo?: {
    name?: string;
    email?: string;
    phone?: string;
    company?: string;
  };
}

const PasswordStrengthIndicator = ({ password, personalInfo }: PasswordStrengthIndicatorProps) => {
  const analysis = useMemo(() => {
    if (!password) return null;
    return analyzePasswordStrength(password, personalInfo);
  }, [password, personalInfo]);

  if (!password || !analysis) return null;

  const getColorClasses = () => {
    switch (analysis.level) {
      case 'excellent':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'strong':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'good':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'fair':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'weak':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getProgressColor = () => {
    switch (analysis.level) {
      case 'excellent':
        return 'bg-green-500';
      case 'strong':
        return 'bg-blue-500';
      case 'good':
        return 'bg-yellow-500';
      case 'fair':
        return 'bg-orange-500';
      case 'weak':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Password Strength</span>
        <Badge variant="outline" className={getColorClasses()}>
          {analysis.level.toUpperCase()}
        </Badge>
      </div>
      
      <Progress 
        value={analysis.score} 
        className="h-2"
        // Apply custom color through CSS custom properties
        style={{ 
          '--progress-foreground': getProgressColor() 
        } as React.CSSProperties}
      />
      
      <div className="text-xs text-muted-foreground">
        Entropy: {Math.round(analysis.entropy)} bits
      </div>

      {analysis.errors.length > 0 && (
        <div className="space-y-1">
          {analysis.errors.map((error, index) => (
            <div key={index} className="flex items-center gap-2 text-xs text-red-600">
              <XCircle className="h-3 w-3" />
              <span>{error}</span>
            </div>
          ))}
        </div>
      )}

      {analysis.suggestions.length > 0 && (
        <div className="space-y-1">
          {analysis.suggestions.map((suggestion, index) => (
            <div key={index} className="flex items-center gap-2 text-xs text-blue-600">
              <AlertCircle className="h-3 w-3" />
              <span>{suggestion}</span>
            </div>
          ))}
        </div>
      )}

      {analysis.isValid && (
        <div className="flex items-center gap-2 text-xs text-green-600">
          <CheckCircle className="h-3 w-3" />
          <span>Password meets security requirements</span>
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;